import { inject, Injectable } from '@angular/core';
import { PutAwayDto, PutAwayListDto } from '@pf/platform-wms/domain';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { asArraySafe, HashObject } from '@pf/shared-utility';
import { PutawayTask } from '../models/putaway-task.model';
import { isHandlingLtl } from '../models/cpw-equipment-types';
import {
    EquipmentStatus,
    InventoryBundleCondition,
    InventoryBundleFragment,
} from '@control-tower/platform-wms';
import { ProfileProvider } from '@pf/shared-common';

@Injectable()
export class PutawayTaskOrchestrator {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly profileProvider = inject(ProfileProvider);

    orchestrate(
        warehouseId: string,
        pendingLists: PutAwayListDto[],
        activeLists: PutAwayListDto[]
    ) {
        const pendingTasks = pendingLists
            .filter(list => this.isStagedOrAssignedToUser(list))
            .map(list => this.createTask(warehouseId, 'pending', list));

        const activeTasks = activeLists.map(list =>
            this.createTask(warehouseId, 'active', list)
        );

        if (activeLists.length > 0) {
            pendingTasks.forEach(task => {
                task.readonly = true;
            });
        }

        this.taskStateService.setTasks([...pendingTasks, ...activeTasks]);
    }

    private isStagedOrAssignedToUser(list: PutAwayListDto): boolean {
        const putAways = asArraySafe(list.putAways);
        if (putAways.length === 0) {
            return false;
        }
        if (
            list.equipment?.assignedToUser?.id ===
            this.profileProvider.profile.userId
        ) {
            return true;
        }

        const isLtl =
            !list.equipment || isHandlingLtl(list.equipment.equipmentType);

        if (isLtl) {
            return putAways.some(putAway => !putAway.putAwayByUser);
        }

        return list.equipment?.status === EquipmentStatus.Staged;
    }

    private getInventoryBundles(
        putAway: PutAwayDto
    ): InventoryBundleFragment[] {
        return [
            ...(putAway.inventoryBundle ? [putAway.inventoryBundle] : []),
            ...(putAway.container?.inventoryBundles ?? []),
        ];
    }

    private createTask(
        warehouseId: string,
        section: PutawayTask['section'],
        list: PutAwayListDto
    ): PutawayTask {
        const itemKeys = new Set(
            (list.putAways ?? [])
                .flatMap(p => this.getInventoryBundles(p))
                .map(bundle => {
                    const item = bundle.item;
                    return item?.id ?? item?.sku ?? item?.code ?? undefined;
                })
                .filter((k): k is string => !!k)
        );
        const totalItems = itemKeys.size;
        const id = HashObject([list.id]);
        const isLtl =
            !list.equipment || isHandlingLtl(list.equipment?.equipmentType);
        const isRecovery = (list.putAways ?? []).some(putaway =>
            this.getInventoryBundles(putaway).some(
                bundle => bundle.condition === InventoryBundleCondition.Returned
            )
        );
        const equipmentName = list.equipment?.name ?? undefined;

        const stagingZoneId = list.equipment?.zone?.id;
        const stagingZoneName = list.equipment?.zone?.name;

        const destinationZoneNames = [
            ...new Set(
                (list.putAways ?? [])
                    .map(p => p.putAwayToSlot?.zone?.name)
                    .filter(Boolean) as string[]
            ),
        ];

        const zoneIds = [
            ...new Set([
                ...((list.putAways ?? [])
                    .map(p => p.putAwayToSlot?.zone?.id)
                    .filter(Boolean) as string[]),
                ...(stagingZoneId ? [stagingZoneId] : []),
            ]),
        ];

        const zoneNames = [
            ...new Set([
                ...destinationZoneNames,
                ...(stagingZoneName ? [stagingZoneName] : []),
            ]),
        ];

        const firstPutAway = (list.putAways ?? [])[0];
        const firstBundle = firstPutAway
            ? this.getInventoryBundles(firstPutAway)[0]
            : undefined;
        const palletLpn = isLtl
            ? (list.putAways ?? [])
                  .map(p => p.container?.lpn)
                  .find((lpn): lpn is string => !!lpn) ?? undefined
            : undefined;

        const itemLabel =
            totalItems === 1 && firstBundle?.item?.code
                ? firstBundle.item.code
                : `${totalItems} item${totalItems !== 1 ? 's' : ''}`;
        const zoneCount = destinationZoneNames.length;
        const zoneLabel =
            zoneCount === 1
                ? destinationZoneNames[0]
                : zoneCount > 0
                  ? `${zoneCount} zones`
                  : undefined;
        const singleLineDetail =
            totalItems === 1 || zoneCount === 1
                ? [itemLabel, zoneLabel].filter(Boolean).join(' · ')
                : undefined;

        const isEmptyPallet = isLtl && totalItems === 0;

        return {
            id,
            warehouseId,
            section,
            title: list.name,
            details: `${totalItems} item${totalItems !== 1 ? 's' : ''}`,
            time: list.createdDate ?? '',
            priority: false,
            taskIconType: 'putAway',
            group: isLtl ? 'LTL' : equipmentName ?? 'Parts',
            putAwayList: list,
            readonly: isEmptyPallet,
            context: {
                putAwayListId: list.id,
                assignedHandlingUnitIds: list.equipmentId
                    ? [list.equipmentId]
                    : [],
            },
            zoneIds,
            zoneNames,
            isLtl,
            isRecovery,
            equipmentName,
            itemCount: totalItems,
            palletLpn,
            singleLineDetail,
            isEmptyPallet,
        } as PutawayTask;
    }
}
